// Phase 3 — Read-only override insights from parts_pricing_events.
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { usePartsPricingEvents } from '@/hooks/pricing/usePartsPricingEvents';
import { useShopPricingConfig } from '@/hooks/pricing/useShopPricingConfig';
import { suggestedSellHybrid } from '@/lib/parts-financial/calculator';
// DTC-K: route the 90d insights window anchor through the canonical
// clock so analytics queries are deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

interface Props { shopId: string; }

interface CategoryStat {
  category: string;
  count: number;
  avgDeltaPct: number;
}

export function OverrideInsightsCard({ shopId }: Props) {
  const { config } = useShopPricingConfig(shopId);
  const from = useMemo(() => { const d = clock.now(); d.setDate(d.getDate() - 90); return d; }, []);
  // CSI-P2: aggregateOnly=true excludes lifecycle audit events
  // (event_type IS NOT NULL) so each reapply contributes exactly one
  // delta-vs-suggested data point (the DMC-J1 trigger event), not two.
  // Pre-CSI-P2 this card silently double-counted manual_override_removed
  // reapplies because the CSI-P1 dual-event design writes both a
  // trigger event AND a lifecycle event for the same UPDATE.
  const eventsQ = usePartsPricingEvents({ shopId, from, excludeCanned: true, aggregateOnly: true, limit: 1000 });

  const stats: CategoryStat[] = useMemo(() => {
    const byCat = new Map<string, { sumDelta: number; count: number }>();
    for (const e of eventsQ.data ?? []) {
      if (e.unit_cost == null || e.actual_sell == null) continue;
      const cost = Number(e.unit_cost);
      const actual = Number(e.actual_sell);
      const sug = suggestedSellHybrid(cost, e.category, config).suggested;
      if (!sug) continue;
      const delta = ((actual - sug) / sug) * 100;
      const key = e.category ?? '(uncategorized)';
      if (!byCat.has(key)) byCat.set(key, { sumDelta: 0, count: 0 });
      const b = byCat.get(key)!;
      b.sumDelta += delta;
      b.count += 1;
    }
    return Array.from(byCat.entries())
      .map(([category, b]) => ({
        category,
        count: b.count,
        avgDeltaPct: Number((b.sumDelta / b.count).toFixed(1)),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [eventsQ.data, config]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Override Insights</CardTitle>
        <CardDescription>
          How often actual sell prices diverge from suggestions, by category — last 90 days, canned jobs excluded.
          Read-only — does not auto-adjust pricing.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!stats.length ? (
          <div className="text-sm text-muted-foreground py-4">
            No override data yet — add parts with cost + sell on tickets to populate.
          </div>
        ) : (
          <div className="space-y-2">
            {stats.map((s) => (
              <div key={s.category} className="flex items-center justify-between text-sm border-b pb-1.5 last:border-0">
                <span className="font-medium">{s.category}</span>
                <span className="text-muted-foreground">
                  {s.count} sample{s.count === 1 ? '' : 's'} ·{' '}
                  <span className={s.avgDeltaPct < 0 ? 'text-destructive' : 'text-foreground'}>
                    avg delta {s.avgDeltaPct >= 0 ? '+' : ''}{s.avgDeltaPct}%
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
